import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import WeatherWidget from "@/components/site/WeatherWidget";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import logoUrl from "@/assets/logo-v2.webp";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Why Us" },
  { to: "/properties", label: "Stays" },
  { to: "/fort-lauderdale-cruise-port-hotels", label: "Cruise" },
  { to: "/neighborhood", label: "Guide" },
  { to: "/events", label: "Events" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let last = null;
    const onScroll = () => {
      const next = window.scrollY > 40;
      if (next !== last) {
        last = next;
        setScrolled(next);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-sand/95 backdrop-blur-xl border-b border-emerald/10 shadow-sm"
          : "bg-sand/95 backdrop-blur-xl border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img
            src={logoUrl}
            alt="Double Rainbow Suites — Fort Lauderdale"
            width={223}
            height={256}
            className="h-16 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </Link>

        <div className="hidden md:flex items-center gap-9">
          <WeatherWidget className="mr-1" />
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-sunset ${
                pathname === l.to ? "text-sunset" : "text-emerald"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/properties"
            data-testid="nav-book-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-sunset px-5 py-2.5 text-sm font-medium text-sand transition-colors duration-300 hover:bg-emerald"
          >
            Book a stay
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </Link>
          <LanguageSwitcher variant="desktop" />
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <Link
            to="/properties"
            data-testid="nav-mobile-book-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-sunset px-4 py-2 text-sm font-medium text-sand transition-colors duration-300 active:bg-emerald"
          >
            Book a stay
            <span>→</span>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="text-emerald"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-sand/95 backdrop-blur-xl border-b border-emerald/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              <WeatherWidget />
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-display text-2xl"
                >
                  {l.label}
                </Link>
              ))}
              <div className="pt-2 border-t border-emerald/10 mt-1">
                <LanguageSwitcher variant="mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
